import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { ChefHat, Gift, Sparkles, Download, Loader2, MailCheck } from "lucide-react";

/**
 * Phase 23 — Recipe Gift public landing page.
 * Routes to `/recipe-gift/:qrToken`. Renders a branded splash, captures an
 * email, then surfaces a one-tap "Download your recipe book" CTA which
 * streams a personalised, restaurant-branded PDF.
 */
export default function RecipeGiftLanding() {
  const { qrToken } = useParams();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [optIn, setOptIn] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pdfToken, setPdfToken] = useState("");
  const [alreadyClaimed, setAlreadyClaimed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/recipe-gift/${qrToken}`);
        if (!cancelled) setMeta(data);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e?.response?.data?.detail) || "Recipe gift not found.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [qrToken]);

  const biz = meta?.business || {};
  const accent = biz.brand_color || "#C05746";
  const heroBg = biz.hero_image_url
    ? `linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.7) 100%), url("${biz.hero_image_url}") center/cover`
    : `linear-gradient(135deg, ${accent} 0%, #2A1C18 100%)`;

  const downloadUrl = useMemo(
    () => (pdfToken ? `${API_BASE}/recipe-gift/pdf/${pdfToken}` : ""),
    [pdfToken],
  );

  useEffect(() => {
    document.title = biz.name ? `${biz.name} — Free Recipe Gift` : "Free Recipe Gift";
  }, [biz.name]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post(`/recipe-gift/${qrToken}/claim`, {
        email: email.trim().toLowerCase(),
        name: name.trim() || null,
        opt_in: optIn,
      });
      setPdfToken(data.pdf_token);
      setAlreadyClaimed(!!data.already_claimed);
    } catch (e2) {
      setError(formatApiError(e2?.response?.data?.detail) || "Couldn't save your details. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFAF0]" data-testid="recipe-gift-loading">
        <Loader2 className="w-7 h-7 animate-spin text-stone-500" />
      </div>
    );
  }

  if (error && !meta) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FFFAF0] px-6 text-center" data-testid="recipe-gift-error">
        <Gift className="w-12 h-12 text-stone-400 mb-4" />
        <h1 className="font-serif text-2xl text-stone-800 mb-2">Recipe gift not found</h1>
        <p className="text-stone-500 max-w-md">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFAF0]" data-testid="recipe-gift-landing">
      {/* Hero band */}
      <header
        className="relative text-white px-5 pt-12 pb-16 sm:pt-20 sm:pb-24"
        style={{ background: heroBg }}
      >
        <div className="max-w-2xl mx-auto">
          {biz.logo_url ? (
            <img
              src={biz.logo_url}
              alt={`${biz.name}`}
              className="h-12 sm:h-14 mb-6 drop-shadow-lg"
              data-testid="recipe-gift-logo"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div className="inline-flex items-center gap-2 text-white/80 text-xs tracking-[0.28em] uppercase mb-6">
              <ChefHat className="w-4 h-4" /> {biz.name}
            </div>
          )}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 border border-white/25 text-xs tracking-widest uppercase backdrop-blur-sm">
            <Sparkles className="w-3.5 h-3.5" /> A gift from {biz.name || "our chef"}
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-5 max-w-xl" data-testid="recipe-gift-headline">
            Take our kitchen home.<br />
            <em className="font-normal italic" style={{ color: "#FCD9A8" }}>It's on us.</em>
          </h1>
          <p className="mt-5 text-white/90 text-base sm:text-lg max-w-lg leading-relaxed" data-testid="recipe-gift-pitch">
            {meta?.pitch}
          </p>
        </div>
      </header>

      {/* Capture card */}
      <main className="px-5 -mt-10 sm:-mt-14 pb-20">
        <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.08)] border border-stone-100 p-6 sm:p-8">
          {!pdfToken ? (
            <form onSubmit={submit} className="space-y-4" data-testid="recipe-gift-form">
              <div>
                <h2 className="font-serif text-2xl text-stone-900">Where should we send it?</h2>
                <p className="text-stone-500 text-sm mt-1">
                  Drop your email and we'll unlock a 16-recipe cookbook curated by our chef. No spam — just the recipes.
                </p>
              </div>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-stone-500">Email</span>
                <input
                  data-testid="recipe-gift-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-1 w-full rounded-xl border border-stone-200 px-4 py-3 text-stone-900 outline-none focus:border-stone-400 focus:ring-2 focus:ring-stone-200"
                />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-stone-500">First name (optional)</span>
                <input
                  data-testid="recipe-gift-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Alex"
                  className="mt-1 w-full rounded-xl border border-stone-200 px-4 py-3 text-stone-900 outline-none focus:border-stone-400 focus:ring-2 focus:ring-stone-200"
                />
              </label>
              <label className="flex items-start gap-3 text-sm text-stone-600">
                <input
                  data-testid="recipe-gift-optin"
                  type="checkbox"
                  checked={optIn}
                  onChange={(e) => setOptIn(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-stone-300"
                />
                <span>Send me occasional updates from {biz.name || "this restaurant"}. Unsubscribe any time.</span>
              </label>

              {error && (
                <div className="text-sm text-rose-600" data-testid="recipe-gift-error-text">{error}</div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="recipe-gift-submit"
                className="w-full rounded-full py-3.5 text-white font-medium tracking-wide flex items-center justify-center gap-2 transition-transform hover:translate-y-[-1px] active:translate-y-0 disabled:opacity-60"
                style={{ background: accent }}
              >
                {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>) : (<><Gift className="w-4 h-4" /> Send me the recipes</>)}
              </button>
              <p className="text-[11px] text-stone-400 text-center pt-1">
                Powered by HustlePlate · 80% recipes from {biz.cuisine ? biz.cuisine.replace(/_/g, " ") : "our kitchen"} · 20% chef-curated extras
              </p>
            </form>
          ) : (
            <div className="text-center py-2" data-testid="recipe-gift-success">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 border border-emerald-200 mb-4">
                <MailCheck className="w-7 h-7 text-emerald-600" />
              </div>
              <h2 className="font-serif text-2xl text-stone-900">
                {alreadyClaimed ? "Welcome back" : "You're in"}
              </h2>
              <p className="text-stone-500 mt-2 max-w-sm mx-auto">
                Your cookbook is ready. It's a fully-illustrated PDF — save it, print it, share it with the dinner table.
              </p>
              <a
                href={downloadUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="recipe-gift-download"
                className="inline-flex items-center gap-2 mt-6 px-6 py-3.5 rounded-full text-white font-medium transition-transform hover:translate-y-[-1px]"
                style={{ background: accent }}
              >
                <Download className="w-4 h-4" /> Download recipe book
              </a>
              <p className="text-[11px] text-stone-400 mt-4">
                Tip: open on your phone and tap the share button to AirDrop / WhatsApp to a friend.
              </p>
            </div>
          )}
        </div>

        {/* Footer trust band */}
        <div className="max-w-xl mx-auto mt-6 text-center">
          <p className="text-xs text-stone-400">
            {biz.suburb ? `${biz.suburb}, ` : ""}{biz.state || ""} · We never share your email. Promise.
          </p>
        </div>
      </main>
    </div>
  );
}
